import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import CreateTicketModal from './CreateTicketModal';
import { getSupportTickets, TICKET_STATUS_COLORS } from '../modules/support-tickets/api';

/**
 * OVO-117 — the partner detail's Tickets tab (admin only — partner viewers
 * have their own /partner/tickets portal page). A partner-filtered slice of
 * the global list, with create-prefilled-with-partner.
 */
export default function PartnerTicketsTab({ partner }) {
  const { t } = useTranslation(['routes', 'common']);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(
    async (p = 1) => {
      if (!partner?.id) return;
      setLoading(true);
      try {
        const { response } = await getSupportTickets({
          partnerId: partner.id,
          take: 20,
          skip: (p - 1) * 20,
        });
        setRows(response?.items || []);
        setTotal(response?.total || 0);
      } finally {
        setLoading(false);
      }
    },
    [partner?.id],
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      width: 70,
      render: (id) => <Link to={`/admin/tickets/${id}`}>#{id}</Link>,
    },
    {
      title: t('tickets.status'),
      dataIndex: 'status',
      width: 120,
      render: (s) => <Tag color={TICKET_STATUS_COLORS[s]}>{t(`tickets.statuses.${s}`, s)}</Tag>,
    },
    {
      title: t('tickets.category'),
      dataIndex: 'category',
      width: 170,
      render: (c) => <Tag>{t(`tickets.categories.${c}`, c)}</Tag>,
    },
    {
      title: t('tickets.subject'),
      dataIndex: 'subject',
      render: (v, rec) => (
        <span>
          {rec.internal && <Tag color="purple">{t('tickets.internalTag')}</Tag>} {v}
        </span>
      ),
    },
    {
      title: t('tickets.order'),
      width: 130,
      render: (_, rec) =>
        rec.order?.orderCode ? (
          <Link to={`/admin/orders/${rec.order.orderCode}`}>{rec.order.orderCode}</Link>
        ) : (
          rec.orderCode || '—'
        ),
    },
    {
      title: t('tickets.lastActivity'),
      dataIndex: 'updatedAt',
      width: 150,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—'),
    },
  ];

  return (
    <Card
      title={t('tickets.title')}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          {t('tickets.newTicket')}
        </Button>
      }
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          showSizeChanger: false,
          onChange: (p) => {
            setPage(p);
            load(p);
          },
        }}
      />
      <CreateTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => load(page)}
        prefill={{ partnerId: partner?.id }}
      />
    </Card>
  );
}
